import React, { useState, useEffect } from 'react';
import { Plus, Trash2, QrCode, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';
import communityRegistryApi, { MemberCreatePayload } from '../../services/communityRegistryApi';

interface PersonDraft {
  full_name: string;
  sex: 'male' | 'female' | '';
  age_years: string;
  id_number: string;
  is_pregnant: boolean;
  is_lactating: boolean;
  attending_anc: boolean;
  next_anc_date: string;
  nutrition_at_risk: boolean;
  muac_cm: string;
  has_danger_signs: boolean;
  suspected_illness: boolean;
  confirmed_illness: boolean;
  illness_type: string;
  death_reported: boolean;
  died_at: string;
}

const emptyPerson = (): PersonDraft => ({
  full_name: '',
  sex: '',
  age_years: '',
  id_number: '',
  is_pregnant: false,
  is_lactating: false,
  attending_anc: false,
  next_anc_date: '',
  nutrition_at_risk: false,
  muac_cm: '',
  has_danger_signs: false,
  suspected_illness: false,
  confirmed_illness: false,
  illness_type: '',
  death_reported: false,
  died_at: '',
});

function toMemberFields(p: PersonDraft) {
  return {
    is_pregnant: p.is_pregnant,
    is_lactating: p.is_lactating,
    attending_anc: p.attending_anc,
    next_anc_date: p.next_anc_date || undefined,
    nutrition_at_risk: p.nutrition_at_risk,
    muac_cm: p.muac_cm ? Number(p.muac_cm) : undefined,
    has_danger_signs: p.has_danger_signs,
    suspected_illness: p.suspected_illness,
    confirmed_illness: p.confirmed_illness,
    illness_type: p.illness_type || undefined,
    death_reported: p.death_reported,
    died_at: p.died_at || undefined,
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  initialRegistryCode?: string;
}

const AddHouseholdModal: React.FC<Props> = ({ open, onClose, onCreated, initialRegistryCode }) => {
  const [houseNumber, setHouseNumber] = useState('');
  const [head, setHead] = useState<PersonDraft>(emptyPerson());
  const [headIdNumber, setHeadIdNumber] = useState('');
  const [headExpanded, setHeadExpanded] = useState(false);
  const [dependents, setDependents] = useState<PersonDraft[]>([]);
  const [expandedDependents, setExpandedDependents] = useState<Record<number, boolean>>({});

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setHouseNumber('');
    setHead(emptyPerson());
    setHeadIdNumber('');
    setHeadExpanded(false);
    setDependents([]);
    setExpandedDependents({});
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const addDependentRow = () => setDependents((d) => [...d, emptyPerson()]);
  const removeDependentRow = (idx: number) =>
    setDependents((d) => d.filter((_, i) => i !== idx));
  const updateDependent = (idx: number, patch: Partial<PersonDraft>) =>
    setDependents((d) => d.map((dep, i) => (i === idx ? { ...dep, ...patch } : dep)));
  const toggleDependentExpanded = (idx: number) =>
    setExpandedDependents((e) => ({ ...e, [idx]: !e[idx] }));

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const headValid = houseNumber.trim() && head.full_name.trim() && head.sex && head.age_years && headIdNumber.trim();
  const dependentsValid = dependents.every((d) => d.full_name.trim() && d.sex && d.age_years);
  const canSubmit = !!headValid && dependentsValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const members: MemberCreatePayload[] = [
        {
          full_name: head.full_name.trim(),
          sex: head.sex as 'male' | 'female',
          age_years: Number(head.age_years),
          id_number: headIdNumber.trim(),
          role_in_hh: 'head',
          ...toMemberFields(head),
        },
        ...dependents.map((d) => ({
          full_name: d.full_name.trim(),
          sex: d.sex as 'male' | 'female',
          age_years: Number(d.age_years),
          id_number: d.id_number.trim() || undefined,
          role_in_hh: 'member' as const,
          ...toMemberFields(d),
        })),
      ];
      await communityRegistryApi.createHousehold({
        house_number: houseNumber.trim(),
        head_name: head.full_name.trim(),
        registry_code: initialRegistryCode,
        members,
      });
      reset();
      onCreated();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to create household');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Household</DialogTitle>
        </DialogHeader>

        {initialRegistryCode && (
          <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 p-2 text-sm">
            <QrCode className="h-4 w-4 text-primary" />
            Registry code: <span className="font-mono font-semibold">{initialRegistryCode}</span>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <section className="space-y-3">
            <h4 className="text-sm font-semibold">Head of household</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="house-number">
                  House number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="house-number"
                  value={houseNumber}
                  onChange={(e) => setHouseNumber(e.target.value)}
                  placeholder="e.g. H-014"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="head-name">
                  Head name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="head-name"
                  value={head.full_name}
                  onChange={(e) => setHead((h) => ({ ...h, full_name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-1">
                <Label>
                  Gender <span className="text-destructive">*</span>
                </Label>
                <Select value={head.sex} onValueChange={(v) => setHead((h) => ({ ...h, sex: v as 'male' | 'female' }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="head-age">
                  Age <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="head-age"
                  type="number"
                  min={0}
                  value={head.age_years}
                  onChange={(e) => setHead((h) => ({ ...h, age_years: e.target.value }))}
                  placeholder="Years"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label htmlFor="head-id-number">
                  ID number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="head-id-number"
                  value={headIdNumber}
                  onChange={(e) => setHeadIdNumber(e.target.value)}
                  placeholder="National ID / document number"
                />
              </div>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setHeadExpanded((v) => !v)}
            >
              {headExpanded ? <ChevronUp className="mr-1 h-3.5 w-3.5" /> : <ChevronDown className="mr-1 h-3.5 w-3.5" />}
              MCH / health details
            </Button>
            {headExpanded && <PersonExtraFields person={head} onChange={(patch) => setHead((h) => ({ ...h, ...patch }))} />}
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Members</h4>
              <Button type="button" variant="outline" size="sm" onClick={addDependentRow}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add member
              </Button>
            </div>

            {dependents.length === 0 && (
              <div className="text-sm text-muted-foreground">No additional members added yet.</div>
            )}

            {dependents.map((dep, idx) => (
              <div key={idx} className="space-y-2 rounded-md border p-3">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4 space-y-1">
                    <Label className="text-xs">
                      Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={dep.full_name}
                      onChange={(e) => updateDependent(idx, { full_name: e.target.value })}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="col-span-3 space-y-1">
                    <Label className="text-xs">
                      Gender <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={dep.sex}
                      onValueChange={(v) => updateDependent(idx, { sex: v as 'male' | 'female' })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">
                      Age <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      value={dep.age_years}
                      onChange={(e) => updateDependent(idx, { age_years: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">ID number</Label>
                    <Input
                      value={dep.id_number}
                      onChange={(e) => updateDependent(idx, { id_number: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="col-span-1 flex items-end justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDependentRow(idx)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => toggleDependentExpanded(idx)}
                >
                  {expandedDependents[idx] ? (
                    <ChevronUp className="mr-1 h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="mr-1 h-3.5 w-3.5" />
                  )}
                  MCH / health details
                </Button>
                {expandedDependents[idx] && (
                  <PersonExtraFields person={dep} onChange={(patch) => updateDependent(idx, patch)} />
                )}
              </div>
            ))}
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Saving…' : 'Save Household'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PersonExtraFields: React.FC<{
  person: PersonDraft;
  onChange: (patch: Partial<PersonDraft>) => void;
}> = ({ person, onChange }) => (
  <div className="space-y-3 rounded-md bg-muted/40 p-3">
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <CheckField label="Pregnant" checked={person.is_pregnant} onChange={(v) => onChange({ is_pregnant: v })} />
      <CheckField label="Lactating" checked={person.is_lactating} onChange={(v) => onChange({ is_lactating: v })} />
      <CheckField
        label="Attending ANC"
        checked={person.attending_anc}
        onChange={(v) => onChange({ attending_anc: v })}
      />
      <CheckField
        label="Nutrition at risk"
        checked={person.nutrition_at_risk}
        onChange={(v) => onChange({ nutrition_at_risk: v })}
      />
      <CheckField
        label="Danger signs"
        checked={person.has_danger_signs}
        onChange={(v) => onChange({ has_danger_signs: v })}
      />
      <CheckField
        label="Suspected illness"
        checked={person.suspected_illness}
        onChange={(v) => onChange({ suspected_illness: v })}
      />
      <CheckField
        label="Confirmed illness"
        checked={person.confirmed_illness}
        onChange={(v) => onChange({ confirmed_illness: v })}
      />
      <CheckField
        label="Death reported"
        checked={person.death_reported}
        onChange={(v) => onChange({ death_reported: v })}
      />
    </div>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {person.attending_anc && (
        <div className="space-y-1">
          <Label className="text-xs">Next ANC date</Label>
          <Input
            type="date"
            value={person.next_anc_date}
            onChange={(e) => onChange({ next_anc_date: e.target.value })}
          />
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">MUAC (cm)</Label>
        <Input
          type="number"
          step="0.1"
          value={person.muac_cm}
          onChange={(e) => onChange({ muac_cm: e.target.value })}
        />
      </div>
      {(person.suspected_illness || person.confirmed_illness) && (
        <div className="space-y-1">
          <Label className="text-xs">Illness type</Label>
          <Input
            value={person.illness_type}
            onChange={(e) => onChange({ illness_type: e.target.value })}
            placeholder="e.g. malaria"
          />
        </div>
      )}
      {person.death_reported && (
        <div className="space-y-1">
          <Label className="text-xs">Date of death</Label>
          <Input type="date" value={person.died_at} onChange={(e) => onChange({ died_at: e.target.value })} />
        </div>
      )}
    </div>
  </div>
);

const CheckField: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({
  label,
  checked,
  onChange,
}) => (
  <label className="flex items-center gap-2 text-sm">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
    {label}
  </label>
);

export default AddHouseholdModal;
